import axios from 'axios';
import '../Sys/config.js';
import { getImpersonationTargetUsername, impersonationWriteBlock, isImpersonating } from './impersonationSession';

var apiUrl = global.platformURI;

export class UserServices {
  constructor() {
    axios.interceptors.request.use(
      function (config) {
        let token = localStorage.getItem('ssoToken');
        config.headers.Authorization = `Token ${token}`;
        config.baseURL = apiUrl;
        return config;
      },
      error => {
        console.log('error.response.status', error);
        return error;
      }
    );
  }

  getAllUser(url) {
    return axios.get(url).then(res => res.data);
  }
  getAllProducts(url) {
    return axios.get(url).then(res => res.data);
  }
  addUser(payload, uuid) {
    const blocked = impersonationWriteBlock();
    if (blocked) return Promise.resolve(blocked);
    if (uuid) {
      return axios.put(`main/userdetail/${uuid}`, payload)
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
    }
    return axios
      .post('main/usercreate', payload)
      .then(res => res.data).catch(res => res.response.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }
  addMultipleUser(payload, uuid) {
    const blocked = impersonationWriteBlock();
    if (blocked) return Promise.resolve(blocked);
    if (uuid) {
      return axios.put(`main/userdetail/${uuid}`, payload)
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
    }
    return axios
      .post('main/usercreatemultiple', payload)
      .then(res => res.data).catch(res => res.response.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }
  getUserDetail(id) {
    return axios.get(`main/userdetail/${id}`).then(res => res.data);
  }

  // my profile get detail
  getDetailMyProfile() {
    let token = localStorage.getItem('ssoToken');
    const body = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${token}`
      }
    };
    return axios.get(`main/myprofile`, body).then(res => res.data);
  }
  updateProfile(payload) {
    const body = { ...(payload || {}) };
    if (isImpersonating()) {
      delete body.email_host_password;
      delete body.password;
      delete body.old_password;
      delete body.new_password;
      delete body.confirm_password;
    }
    return axios
      .put(`main/editprofile`, body)
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }
  changeCompanyLogo(id) {
    return axios.get(`main/customerlogo/${id}`).then(res => res.data);
  }

  // Bulk upload
  getBulkTemplate(uuid, { projectId, programmeType } = {}) {
    let comp_ids = uuid || localStorage.getItem('comp_ids') || localStorage.getItem('uuid');
    const params = new URLSearchParams();
    if (projectId) params.set('project_id', projectId);
    else if (programmeType) params.set('programme_type', programmeType);
    else if (comp_ids) params.set('customer_id', comp_ids);
    return axios
      .get(`main/bulkuploadtemplate${params.toString() ? `?${params.toString()}` : ''}`)
      .then(res => res.data);
  }
  getBulkUploadStatus(jobId) {
    return axios
      .get(`main/bulkuserupload/${encodeURIComponent(jobId)}`)
      .then(res => res.data);
  }

  bulkUpload(payload, uuid) {
    const blocked = impersonationWriteBlock();
    if (blocked) return Promise.resolve(blocked);
    let comp_ids = uuid || localStorage.getItem('comp_ids') || localStorage.getItem('uuid');
    return axios
      .post('main/bulkuserupload?customer_id=' + comp_ids, payload)
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }

  customerAdminUser(id, type) {
    return axios
      .get(
        `main/customer_admin_user?customer_id=` + id + `&type=` + type
      )
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }

  saveCustomerAdminUsers(id, payload) {
    return axios
      .put(`main/customer_admin_user?customer_id=${id}`, payload)
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }

  customerDropdown(id) {
    return axios
      .get(`main/customerdropdown?customer_id=${id}`)
      .then(res => res.data);
  }

  changePassword(payload) {
    const blocked = impersonationWriteBlock();
    if (blocked) return Promise.resolve(blocked);
    return axios.post(`main/changepassword`, payload)
      .then(res => res.data)
      .catch(err => (err.response && err.response.data) ? err.response.data : err);
  }

  getProjectAdminUsers(id) {
    return axios.get(`main/project_admin_users/${id}`).then(res => res.data);
  }
}

let myProfileCache = { key: '', profile: null, request: null };

const myProfileCacheKey = () => {
  const actor = localStorage.getItem('uuid') || localStorage.getItem('user') || '';
  const target = isImpersonating()
    ? (getImpersonationTargetUsername() || 'impersonating')
    : 'self';
  return `${actor}:${target}`;
};

export const clearMyProfileCache = () => {
  myProfileCache = { key: '', profile: null, request: null };
};

export const peekMyProfileCache = () => {
  const key = myProfileCacheKey();
  if (myProfileCache.profile && myProfileCache.key === key) {
    return myProfileCache.profile;
  }
  return null;
};

export const getCachedMyProfile = (userServices) => {
  const key = myProfileCacheKey();
  if (myProfileCache.profile && myProfileCache.key === key) {
    return Promise.resolve(myProfileCache.profile);
  }
  if (!myProfileCache.request || myProfileCache.key !== key) {
    myProfileCache = { key, profile: null, request: null };
    myProfileCache.request = userServices.getDetailMyProfile()
      .then((user) => {
        const profile = user?.data?.[0] || null;
        if (myProfileCache.key === key) {
          myProfileCache.profile = profile;
        }
        return profile;
      })
      .catch((error) => {
        if (myProfileCache.key === key) {
          myProfileCache.request = null;
          myProfileCache.key = '';
        }
        return Promise.reject(error);
      });
  }
  return myProfileCache.request;
};
